import { ADDON_MAX_UNITS, DEV_CHECKOUT_PATH_PATTERN, FEATURE_ADDON_KEYS, PLAN_KEYS, USAGE_WARN_RATIO, type FeatureAddonKey, type LaunchTier, type PaymentGatewayKey } from "@sagot/constants";
import type { BadgeTone, IconName } from "@sagot/ui";
import type { AddonCatalogOut, BillingConfigOut, BillingPlan, InvoiceOut, OrderOut, PlanKeyOut } from "../shared";

const MB_PER_GB = 1024;

export type BillingInterval = "monthly" | "annual";

/** What to do with an order the API returned (plan change, checkout, add-on, invoice payment). */
export type CheckoutAction =
  | { kind: "dev"; paymentId: string }
  | { kind: "redirect"; url: string }
  | { kind: "charged" }
  | { kind: "failed" }
  | { kind: "scheduled" }
  | { kind: "none" };

/** MockPayments (development) answers with a console path instead of a PayMongo page. */
export function devCheckoutPaymentId(url: string | null | undefined): string | null {
  if (!url) return null;
  let path: string;
  try {
    path = new URL(url, "https://placeholder.invalid").pathname;
  } catch {
    return null;
  }
  return DEV_CHECKOUT_PATH_PATTERN.exec(path)?.[1] ?? null;
}

/** Only https PSP pages are followed (never javascript:/data: or plain http). */
function safeRedirect(url: string): string | null {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" ? parsed.href : null;
  } catch {
    return null;
  }
}

export function checkoutAction(order: OrderOut): CheckoutAction {
  if (order.status === "scheduled") return { kind: "scheduled" };
  if (order.status === "failed") return { kind: "failed" };
  const devId = devCheckoutPaymentId(order.checkout_url) ?? null;
  if (devId) return { kind: "dev", paymentId: devId };
  if (order.checkout_url) {
    const url = safeRedirect(order.checkout_url);
    return url ? { kind: "redirect", url } : { kind: "none" };
  }
  if (order.off_session) return { kind: "charged" };
  return { kind: "none" };
}

export interface MeterView {
  /** 0–100 for the bar; 0 when unlimited. */
  percent: number;
  unlimited: boolean;
  /** Used ≥ limit (seats full) or projection past the allowance. */
  warn: boolean;
}

export function meterView(used: number, limit: number | null | undefined, projected?: number): MeterView {
  if (limit === null || limit === undefined || limit <= 0) return { percent: 0, unlimited: true, warn: false };
  const percent = Math.min((used / limit) * 100, 100);
  const overByProjection = projected !== undefined && projected > limit;
  return { percent, unlimited: false, warn: overByProjection || used / limit >= USAGE_WARN_RATIO };
}

/** Price for the chosen interval as a number (plan currency), `null` for quoted (custom) plans. */
export function planPrice(plan: Pick<BillingPlan, "price_monthly" | "price_annual">, interval: BillingInterval): number | null {
  const raw = interval === "annual" ? plan.price_annual : plan.price_monthly;
  if (raw === null) return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

export interface TaxView {
  mode: string;
  rate: string;
  label: string;
}

/** Tax added on top of an exclusive price (PH VAT); 0 when inclusive, zero-rated or exempt. */
export function taxOn(amount: number, tax: TaxView): number {
  const rate = Number(tax.rate);
  if (tax.mode !== "exclusive" || !Number.isFinite(rate) || rate <= 0) return 0;
  return Math.round(amount * rate * 100) / 100;
}

export interface GatewayChoice {
  gateway: PaymentGatewayKey;
  /** i18n key suffix: `billing.gateway.<labelKey>`. */
  labelKey: "paymongo" | "stripe" | "paypal";
}

const GATEWAY_ORDER: readonly PaymentGatewayKey[] = ["paymongo", "stripe", "paypal"];

/**
 * Checkout options for the period currency (DEC-042): the API lists the gateways that collect it
 * (`overview.gateways`); PHP has only PayMongo (no choice shown), USD lets the payer pick.
 */
export function gatewayChoices(gateways: readonly string[]): GatewayChoice[] {
  return GATEWAY_ORDER.filter((gateway) => gateways.includes(gateway)).map((gateway) => ({ gateway, labelKey: gateway }));
}

/** Whether the payer must choose (more than one gateway collects the currency). */
export function needsGatewayChoice(gateways: readonly string[]): boolean {
  return gatewayChoices(gateways).length > 1;
}

/** Months the annual price saves vs paying monthly (mockup: "2 months free"). */
export function annualMonthsFree(plan: Pick<BillingPlan, "price_monthly" | "price_annual">): number {
  const monthly = planPrice(plan, "monthly");
  const annual = planPrice(plan, "annual");
  if (!monthly || !annual) return 0;
  return Math.max(Math.round(12 - annual / monthly), 0);
}

/** The plan feature each feature add-on grants: Lite the launch, Plus (and Institution) the launch with customer facts. */
const ADDON_FEATURE: Readonly<Record<FeatureAddonKey, string>> = { secure_launch_lite: "secure_launch", secure_launch: "secure_launch_facts" };

/** Which Secure launch a workspace has, read from its plan features (Institution has both features). */
export function launchTierOf(features: readonly string[]): LaunchTier {
  if (!features.includes("secure_launch")) return "none";
  return features.includes("secure_launch_facts") ? "plus" : "lite";
}

/**
 * Feature add-ons Billing offers: the catalogue sells it (active, priced in the billing currency), on the
 * current plan, and the plan does not already have what it grants. Lite is offered while there is no launch at all;
 * Plus until the facts are there, so a Lite workspace is offered Plus (it replaces Lite) and a Plus or Institution
 * workspace is offered neither (a downgrade is refused until Plus has ended).
 */
export function offeredFeatureAddons(
  catalog: readonly Pick<AddonCatalogOut, "key" | "available_plans">[],
  planKey: string,
  features: readonly string[],
): FeatureAddonKey[] {
  return FEATURE_ADDON_KEYS.filter((key) => {
    const row = catalog.find((addon) => addon.key === key);
    if (!row || features.includes(ADDON_FEATURE[key])) return false;
    return row.available_plans.length === 0 || row.available_plans.includes(planKey);
  });
}

/** Features the next plan up adds (shown as "not on your plan"). */
export function missingFeatures(current: readonly string[], plans: readonly BillingPlan[], currentKey: string): string[] {
  const order = plans.filter((p) => !p.is_custom);
  const index = order.findIndex((p) => p.key === currentKey);
  const next = index >= 0 ? order[index + 1] : undefined;
  if (!next) return [];
  return next.features.filter((feature) => !current.includes(feature));
}

/** Features a plan adds over the plan before it in the list (the "+" line on plan cards). */
export function addedFeatures(plans: readonly BillingPlan[], index: number): string[] {
  const plan = plans[index];
  if (!plan) return [];
  const previous = index > 0 ? plans[index - 1] : undefined;
  return previous ? plan.features.filter((f) => !previous.features.includes(f)) : plan.features;
}

export type PlanDirection = "current" | "upgrade" | "downgrade" | "custom";

export function planDirection(plans: readonly BillingPlan[], currentKey: string, targetKey: string): PlanDirection {
  const target = plans.find((p) => p.key === targetKey);
  if (target?.is_custom) return "custom";
  if (targetKey === currentKey) return "current";
  const rank = (key: string) => plans.findIndex((p) => p.key === key);
  return rank(targetKey) > rank(currentKey) ? "upgrade" : "downgrade";
}

export interface InvoiceStatusView {
  key: "paid" | "due" | "overdue" | "failed" | "void" | "draft";
  tone: BadgeTone;
  icon: IconName;
  payable: boolean;
}

const STATUS_VIEWS: Record<InvoiceStatusView["key"], Omit<InvoiceStatusView, "key">> = {
  paid: { tone: "success", icon: "check-circle", payable: false },
  due: { tone: "warning", icon: "clock", payable: true },
  overdue: { tone: "danger", icon: "alert-triangle", payable: true },
  failed: { tone: "danger", icon: "x-circle", payable: true },
  void: { tone: "neutral", icon: "circle", payable: false },
  draft: { tone: "neutral", icon: "circle", payable: false },
};

export function invoiceStatus(invoice: Pick<InvoiceOut, "status" | "due_date">, now: Date): InvoiceStatusView {
  const key = invoiceStatusKey(invoice, now);
  return { key, ...STATUS_VIEWS[key] };
}

function invoiceStatusKey(invoice: Pick<InvoiceOut, "status" | "due_date">, now: Date): InvoiceStatusView["key"] {
  switch (invoice.status) {
    case "paid":
    case "paid_late":
      return "paid";
    case "failed":
      return "failed";
    case "void":
      return "void";
    case "open": {
      const due = invoice.due_date ? new Date(`${invoice.due_date}T23:59:59`) : null;
      return due && due.getTime() < now.getTime() ? "overdue" : "due";
    }
    default:
      return "draft";
  }
}

/** RFC 4180 CSV (quotes doubled; every cell quoted so commas in labels are safe). */
export function toCsv(rows: readonly (readonly (string | number)[])[]): string {
  return rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")).join("\r\n");
}

/** Narrows an API plan key to the checkout/change enum (custom keys are quoted by sales). */
export function asPlanKey(key: string): PlanKeyOut | null {
  return (PLAN_KEYS as readonly string[]).includes(key) ? (key as PlanKeyOut) : null;
}

/** `1024` MB → "1 GB", `100` → "100 MB". */
export function storageText(mb: number): string {
  return mb >= MB_PER_GB ? `${String(Math.round((mb / MB_PER_GB) * 10) / 10)} GB` : `${String(mb)} MB`;
}

/** Units of one add-on the workspace holds right now (a cancelled one counts until its period ends). */
export function heldUnits(addons: readonly { key: string; quantity: number }[], key: string): number {
  return addons.filter((addon) => addon.key === key).reduce((total, addon) => total + addon.quantity, 0);
}

/** Units still purchasable before the add-on's per-workspace cap; `null` when it has no cap. */
export function unitsLeft(key: string, held: number): number | null {
  const cap = ADDON_MAX_UNITS[key];
  return cap === undefined ? null : Math.max(cap - held, 0);
}

/** One gateway's entry of `GET /v1/billing/config` (mode + that mode's browser-safe key). */
export function gatewayConfig(config: Pick<BillingConfigOut, "gateways"> | undefined, gateway: string): BillingConfigOut["gateways"][number] | undefined {
  return config?.gateways.find((entry) => entry.gateway === gateway);
}

/** True when any listed gateway runs against its sandbox: no real money moves (DEC-065). */
export function anyTestMode(config: Pick<BillingConfigOut, "gateways"> | undefined, gateways: readonly (string | null | undefined)[]): boolean {
  return gateways.some((gateway) => gateway != null && gatewayConfig(config, gateway)?.mode === "test");
}
